import { wallOpeningPlanExtents } from "building-common/openings/openingQuery";
import type { GeneratedDoorPlacement } from "building-types/doors/doorTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { WallType } from "building-types/walls/wallEnums";
import { type WallGap, WallGapSource, type WallGapDescriptor } from "building-types/walls/wallGap";
import type { WallStrip } from "building-types/walls/wallStrip";
import type { Wall, WallMass } from "building-types/walls/wallTypes";
import type { GeneratedWindowPlacement } from "building-types/windows/windowTypes";
import { assertIsNotNullOrUndefined, assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import repeat from "common/utils/repeat";
import { wallGapDescriptorNew, wallGapNew } from "doc/utils/wallGapTools";
import { closestPointPolyLine2, closestParamNormalizedLine2 } from "geom/closestPoint";
import { DIST_EPSILON, DIST_EPSILON_SQUARED } from "geom/core";
import { joinPolyLine2Sequence } from "geom/join";
import { offsetPolyLine2Variable } from "geom/offset";
import { polyLine2MergeCollinearEdges, polyLine2EdgeCount, polyLine2Centroid } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import { pushXY } from "geom/utils";
import { vec2DistSquared, vec2Dist, vec2Lerp, vec2Dot } from "geom/vec2";
import { getWindowHeight } from "../window/windowHeightTools";
import { makeWallGap } from "./solidWall";
import { wallsInRoomAreParallel, wallCenterLineStart, wallCenterLineEnd } from "./wall";
import { wallBaseLine, wallBaselineOffset } from "./wallBaseline";

export class WallStripImpl implements WallStrip {
  constructor(
    readonly room: Room,
    readonly walls: (Wall | WallMass)[],
    readonly startIndex: number,
    readonly count: number,
  ) {
    this.startIndex = startIndex % walls.length;
  }

  firstWall() {
    return this.walls[this.startIndex];
  }

  firstWallIndexInRoom() {
    return this.startIndex;
  }

  lastWallIndexInRoom() {
    return (this.startIndex + this.count - 1) % this.walls.length;
  }

  lastWall() {
    return this.walls[this.lastWallIndexInRoom()];
  }

  toStraightStrips(includeWallOffset?: boolean): WallStrip[] {
    const { room, walls, count, startIndex } = this;

    const strips: WallStrip[] = [];

    let lastWall: Wall | undefined;
    let lastOffset: number = 0;
    let curStrip;

    for (let i = 0; i < count; i++) {
      const wall = walls[(startIndex + i) % walls.length];
      const offset = wallBaselineOffset(wall, room);

      if (
        lastWall &&
        wallsInRoomAreParallel(room, lastWall, wall) &&
        (!includeWallOffset || Math.abs(offset - lastOffset) < DIST_EPSILON)
      ) {
        // append
        curStrip.count++;
      } else {
        // reset
        curStrip = wallStripNew(room, walls, startIndex + i, 1);
        strips.push(curStrip);
      }

      lastWall = wall;
      lastOffset = offset;
    }

    return strips;
  }

  centerLine(removeCollinearNeighbors: boolean = false) {
    const { room, walls, count, startIndex } = this;
    const pl = polyLine2New((count + 1) * 2);

    for (let i = 0, l = count; i < l; i++) {
      const wall = walls[(startIndex + i) % walls.length];

      const [firstX, firstY] = wallCenterLineStart(room, wall);

      const i2 = i * 2;

      pl[i2] = firstX;
      pl[i2 + 1] = firstY;

      if (i === l - 1) {
        const [lastX, lastY] = wallCenterLineEnd(room, wall);

        pl[i2 + 2] = lastX;
        pl[i2 + 3] = lastY;
      }
    }

    return removeCollinearNeighbors ? polyLine2MergeCollinearEdges(pl) : pl;
  }

  closestWall(x: number, y: number): { wall: Wall; x: number; y: number; t: number; dist: number } | null {
    const baseline = this.centerLine();

    const ref = closestPointPolyLine2(baseline, x, y);

    if (!ref) {
      return null;
    }

    return {
      wall: this.walls[this.startIndex + ref.edgeIndex],
      x: ref.x,
      y: ref.y,
      t: ref.t,
      dist: ref.dist,
    };
  }

  static allToStraightStrips(inputStrips: WallStrip[], includeWallOffset?: boolean): WallStrip[] {
    const strips: WallStrip[] = [];

    for (let i = 0, li = inputStrips.length; i < li; i++) {
      strips.push(...inputStrips[i].toStraightStrips(includeWallOffset));
    }

    return strips;
  }

  static allCenterLines(wallStrips: WallStrip[], removeCollinearNeighbors: boolean = false): PolyLine2[] {
    const segments: PolyLine2[] = [];

    for (let i = 0, li = wallStrips.length; i < li; i++) {
      segments.push(wallStrips[i].centerLine(removeCollinearNeighbors));
    }

    return segments;
  }

  static allEndPoints(wallStrips: WallStrip[]): PolyLine2[] {
    const segments: PolyLine2[] = [];

    for (const { room, count, walls, startIndex } of wallStrips) {
      const pl = polyLine2New(4);
      segments.push(pl);

      const firstWall = walls[startIndex];
      const lastWall = walls[(startIndex + count - 1) % walls.length];

      const [firstX, firstY] = wallCenterLineStart(room, firstWall);

      pl[0] = firstX;
      pl[1] = firstY;

      const [lastX, lastY] = wallCenterLineEnd(room, lastWall);

      pl[2] = lastX;
      pl[3] = lastY;
    }

    return segments;
  }

  /** Same as baseline, but returns info about what wall produced the edge.  Edges that are produced by
   * differences in wall thickness currently have "no wall" associated with them.
   */
  baselineWithEdgeInfo(
    removeCollinearNeighbors: boolean = false,
    closePolyLine: boolean = false,
    removeShortSegmentsAtEnds: boolean = false,
  ): { pl: PolyLine2; edgeToWallId: string[] } {
    const { room, walls, count, startIndex } = this;
    const path: number[] = [];
    const edgeToWallId: string[] = [];
    let hasShortSegment = false;
    let newZeroIndex: number | undefined;

    for (let i = 0; i < count; i++) {
      const idx = (startIndex + i) % walls.length;

      const l = wallBaseLine(room, walls, idx, removeShortSegmentsAtEnds);
      if (removeShortSegmentsAtEnds && l === null && (i === 0 || i === count - 1)) {
        hasShortSegment = true;
        newZeroIndex = i + 1;
        continue;
      }

      assertIsNotNullOrUndefined(l);

      if (
        i === 0 ||
        (hasShortSegment && i === newZeroIndex) ||
        vec2DistSquared(l[0], l[1], path[path.length - 2], path[path.length - 1]) > DIST_EPSILON_SQUARED
      ) {
        pushXY(path, l[0], l[1]);
        if (i !== 0) {
          edgeToWallId.push(""); // this edge produced by difference in wall thicknesses.
        }
      }

      pushXY(path, l[2], l[3]);
      edgeToWallId.push(walls[idx].id);
    }

    if (
      closePolyLine &&
      vec2DistSquared(path[0], path[1], path[path.length - 2], path[path.length - 1]) > DIST_EPSILON_SQUARED
    ) {
      pushXY(path, path[0], path[1]);
      edgeToWallId.push("");
    }

    const pl = polyLine2New(path);
    return {
      pl: removeCollinearNeighbors ? polyLine2MergeCollinearEdges(pl) : pl,
      edgeToWallId,
    };
  }

  /** baseLine gets the base of the given wall strip on the interior of the given room */
  baseline(
    removeCollinearNeighbors: boolean = false,
    closePolyLine: boolean = false,
    removeShortSegmentsAtEnds: boolean = false,
  ): PolyLine2 {
    const { pl } = this.baselineWithEdgeInfo(removeCollinearNeighbors, closePolyLine, removeShortSegmentsAtEnds);
    return pl;
  }

  /**
   * baseLineMaxOffset gets the base of the given wall strip, but ensures that collinear wall substrips are offset
   * the maximum of their offset so the path is consistently parallel to the strip center lines.
   */
  baselineMaxOffset(removeCollinearNeighbors: boolean = false): PolyLine2 {
    // is this strip closed? if not, expand each end by 1
    let straightStrips: WallStrip[];

    if (this.closed()) {
      straightStrips = this.toStraightStrips();
    } else {
      const s = new WallStripImpl(
        this.room,
        this.walls,
        (this.startIndex - 1 + this.walls.length) % this.walls.length,
        this.count + 2,
      );
      straightStrips = s.toStraightStrips();
    }

    const offsetDists = straightStrips.map((x) => x.largestWallOffset());

    // This is a tricky bit of code. Generally, we want to join collinear edges before offsetting
    // because short collinear segments are poorly behaved when offsetting. They can result in edge reversals
    // particularly when you have a short edge right next to a turning point.
    // This code tries to join straight edges where practical to avoid this situation.
    const centerLines = straightStrips.map((x) =>
      x.centerLine(x.count === this.count && x.startIndex === this.startIndex),
    );

    const finalOffsetDists: number[] = [];
    offsetDists.forEach((dist, i) => {
      finalOffsetDists.push(...repeat(polyLine2EdgeCount(centerLines[i]), dist));
    });

    const pathWithoutOffset = joinPolyLine2Sequence(centerLines);

    let path = offsetPolyLine2Variable(
      pathWithoutOffset,
      finalOffsetDists.map((x) => -x),
    );

    if (!this.closed()) {
      path = path.slice(2, path.length - 2);
    }

    return removeCollinearNeighbors ? polyLine2MergeCollinearEdges(path) : path;
  }

  closed() {
    return this.count === this.walls.length;
  }

  largestWallOffset() {
    const { walls, count, startIndex } = this;
    let largest = 0;
    for (let i = 0; i < count; i++) {
      const wall = walls[(startIndex + i) % walls.length];
      if (!wall) {
        continue;
      }
      const o = wallBaselineOffset(wall, this.room);
      if (Math.abs(o) > Math.abs(largest)) {
        largest = o;
      }
    }
    return largest;
  }

  wallOffsets() {
    const { walls, count, startIndex } = this;
    const offsets: number[] = [];
    for (let i = 0; i < count; i++) {
      const wall = walls[(startIndex + i) % walls.length];
      if (!wall) {
        continue;
      }
      offsets.push(wallBaselineOffset(wall, this.room));
    }
    return offsets;
  }

  toWallArray(): Wall[] {
    const { walls, count, startIndex } = this;
    const res = new Array<Wall>(count);
    for (let i = 0; i < count; i++) {
      res[i] = walls[(startIndex + i) % walls.length];
    }
    return res;
  }

  doors(): GeneratedDoorPlacement[] {
    const { count, startIndex, walls } = this;
    const doors: GeneratedDoorPlacement[] = [];

    for (let i = 0; i < count; i++) {
      const wall = walls[(startIndex + i) % walls.length];

      if (wall.wallType === WallType.VOID) {
        // a void wall is a open wall
      } else if (wall.wallType === WallType.SOLID) {
        // a door is a void wall
        doors.push(...wall.doorPlacements);
      }
    }

    return doors;
  }

  doorGaps(normalized: boolean = true): WallGap[] {
    const params: WallGap[] = [];
    const doors = this.doors();
    const pl = this.baseline(true);

    for (let i = 0, li = doors.length; i < li; i++) {
      const door = doors[i];
      const { opening } = door;

      assertIsNotUndefined(opening);

      params.push(
        makeWallGap(
          doors,
          pl,
          wallOpeningPlanExtents(opening),
          0.0,
          door.descriptor.height,
          normalized,
          0,
          false,
          WallGapSource.DOOR,
        ),
      );
    }

    return params;
  }

  doorGapDescriptors(normalized: boolean = true): WallGapDescriptor[] {
    const params: WallGapDescriptor[] = [];
    const doors = this.doors();
    const pl = this.baseline(true);

    // console.assert(pl.length === 4, "WallStrip.doorGapDescriptors assumes the strip is straight");
    const ax = pl[0];
    const ay = pl[1];

    const bx = pl[2];
    const by = pl[3];

    const len = vec2Dist(ax, ay, bx, by);

    for (let i = 0, li = doors.length; i < li; i++) {
      const door = doors[i];
      const { opening } = door;

      assertIsNotUndefined(opening);

      const planOutline = wallOpeningPlanExtents(opening);

      const [startX, startY] = vec2Lerp(planOutline[2], planOutline[3], planOutline[4], planOutline[5], 0.5);
      const [centerX, centerY] = ensureDefined(polyLine2Centroid(planOutline));
      const [endX, endY] = vec2Lerp(planOutline[6], planOutline[7], planOutline[8], planOutline[9], 0.5);

      let startT = closestParamNormalizedLine2(ax, ay, bx, by, startX, startY);
      let centerT = closestParamNormalizedLine2(ax, ay, bx, by, centerX, centerY);
      let endT = closestParamNormalizedLine2(ax, ay, bx, by, endX, endY);

      if (vec2Dot(bx - ax, by - ay, endX - startX, endY - startY) < 0.0) {
        const tmp = startT;
        startT = endT;
        endT = tmp;
      }

      if (!normalized) {
        startT *= len;
        centerT *= len;
        endT *= len;
      }

      params.push(wallGapDescriptorNew(startT, centerT, endT, 0.0, door.descriptor.height, door.id));
    }

    return params;
  }

  doorGap(doorId: string, normalized: boolean = true): WallGap | null {
    const doors = this.doors();
    const pl = this.baseline(true);

    // console.assert(pl.length === 4, "WallStrip.doorGap assumes the strip is straight");
    const ax = pl[0];
    const ay = pl[1];

    const bx = pl[2];
    const by = pl[3];

    const len = vec2Dist(ax, ay, bx, by);

    for (let i = 0, li = doors.length; i < li; i++) {
      const door = doors[i];
      const { opening, id } = door;
      if (id !== doorId) {
        continue;
      }

      assertIsNotUndefined(opening);

      const planOutline = wallOpeningPlanExtents(opening);

      const [startX, startY] = vec2Lerp(planOutline[2], planOutline[3], planOutline[4], planOutline[5], 0.5);
      const [centerX, centerY] = ensureDefined(polyLine2Centroid(planOutline));
      const [endX, endY] = vec2Lerp(planOutline[6], planOutline[7], planOutline[8], planOutline[9], 0.5);

      let startT = closestParamNormalizedLine2(ax, ay, bx, by, startX, startY);
      let centerT = closestParamNormalizedLine2(ax, ay, bx, by, centerX, centerY);
      let endT = closestParamNormalizedLine2(ax, ay, bx, by, endX, endY);

      if (vec2Dot(bx - ax, by - ay, endX - startX, endY - startY) < 0.0) {
        const tmp = startT;
        startT = endT;
        endT = tmp;
      }

      if (!normalized) {
        startT *= len;
        centerT *= len;
        endT *= len;
      }

      return wallGapNew(
        [door],
        startT,
        centerT,
        endT,
        0.0,
        door.descriptor.height,
        false,
        false,
        false,
        WallGapSource.DOOR,
      );
    }

    return null;
  }

  windows(): GeneratedWindowPlacement[] {
    const { count, startIndex, walls } = this;
    const windows: GeneratedWindowPlacement[] = [];

    for (let i = 0; i < count; i++) {
      const wall = walls[(startIndex + i) % walls.length];

      if (wall.wallType === WallType.VOID) {
        // a void wall is a open wall
      } else if (wall.wallType === WallType.SOLID) {
        // a door is a void wall
        windows.push(...wall.windowPlacements);
      }
    }

    return windows;
  }

  windowGaps(normalized: boolean = true): WallGap[] {
    const params: WallGap[] = [];
    const windows = this.windows();
    const pl = this.baseline(true);

    for (let i = 0, li = windows.length; i < li; i++) {
      const window = windows[i];

      assertIsNotUndefined(window.opening);

      const height = getWindowHeight(window.descriptor);
      const bottom = ensureDefined(window.headHeight) - height;

      params.push(
        makeWallGap(
          [window],
          pl,
          wallOpeningPlanExtents(window.opening),
          bottom,
          height,
          normalized,
          0,
          false,
          WallGapSource.WINDOW,
        ),
      );
    }

    return params;
  }
}
export function wallStripNew(room: Room, walls: Wall[], startIndex: number, count: number) {
  return new WallStripImpl(room, walls, startIndex, count);
}
